import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Spin, Tabs } from "antd";
import { usePDF } from "react-to-pdf";
import { clsx } from "clsx";
import {
    Period,
    SupplierFullCompetitor,
    SupplierInsight,
    SupplierWithEmployee,
} from "../../../types/SupplierTypes";
import { EmployeeCount, Ratio, Quote, FinancialStatements } from "../../../types/FinanceTypes";
import { OrderForm } from "../../../types/OrderFormTypes";
import { isPocOnly, isSuperAdmin } from "../../../types/UserGroup";
import {
    updateSupplierAnalysis,
    getSupplierById,
    getSupplierCompetitors,
    getSupplierInsights,
    updateSupplierCompetitor,
    getSupplierOrder,
} from "../../../common/api/SupplierClient";
import {
    getEmployeeCountsBySymbol,
    getFinancialStatementsBySymbol,
    getInsightsBySymbol,
    getQuotesBySymbol,
    getRatiosBySymbol,
} from "../../../common/api/FinanceClient";
import { handleError } from "../../../common/ErrorHandling";
import { useUserState } from "../../../common/UserContext";
import { CompetitorEditModal } from "./components/CompetitorEditModal";
import { FinancialNewsProps } from "./components/FinancialNews";
import { SupplierBase } from "./components/SupplierBase";
import { SupplierDetailTopPanel } from "./SupplierDetailTopPanel";
import { SupplierOverviewPanel } from "./SupplierOverviewPanel";
import { SupplierRatiosPanel } from "./SupplierRatiosPanel";
import { SupplierStatementsPanel } from "./SupplierStatementsPanel";
import { LeverageSummary } from "./components/LeverageSummary";
import { MarketTrendsPanel } from "./MarketTrendsPanel";
import { BenchmarkingPanel } from "./BenchmarkingPanel";
import { roundToTwoDecimals } from "../../../common/Number";
import { getOrganization } from "../../../common/api/UserClient";
import { getScoreGrade } from "../../../common/Grade";
import { PdfPanel } from "./PdfPanel";
import classes from "./SupplierDetailView.module.less";

enum TabKey {
    Overview = "overview",
    Market = "market",
    Benchmark = "benchmark",
    Health = "health",
    Statements = "statements",
}

export const SupplierDetailView: React.FC = () => {
    const params = useParams();
    const { me } = useUserState();
    const [supplierId] = useState(Number(params.id));
    const [supplierProfile, setSupplierProfile] = useState({} as SupplierWithEmployee);
    const [employees, setEmployees] = useState([] as EmployeeCount[]);
    const [supplierCompetitors, setSupplierCompetitors] = useState([] as SupplierFullCompetitor[]);
    const [supplierInsights, setSupplierInsights] = useState([] as SupplierInsight[]);
    const [period, setPeriod] = useState(Period.Annually);
    const [supplierNews, setSupplierNews] = useState({} as FinancialNewsProps);
    const { toPDF, targetRef } = usePDF({
        canvas: { useCORS: true },
        page: { format: [323.85, 419.1] },
    });

    const [competitor, setCompetitor] = useState({} as SupplierFullCompetitor);
    const [showCompetitorModal, setShowCompetitorModal] = useState(false);
    const [ratios, setRatios] = useState([] as Ratio[]);
    const [quotes, setQuotes] = useState([] as Quote[]);
    const [financialStatements, setFinancialStatements] = useState({} as FinancialStatements);
    const [orderForm, setOrderForm] = useState<OrderForm>();
    const [loading, setLoading] = useState(true);
    const [orgName, setOrgName] = useState("");

    const createPdf = async () => {
        try {
            await toPDF();
        } catch (error) {
            handleError(error);
        }
    };

    const refreshSupplier = async (id: number) => {
        setLoading(true);
        try {
            const [profile, competitors, insights, order] = await Promise.all([
                getSupplierById(id),
                getSupplierCompetitors(id),
                getSupplierInsights(id),
                getSupplierOrder(id),
            ]);
            setSupplierProfile(profile);
            setSupplierCompetitors(competitors);
            setSupplierInsights(insights);
            setOrderForm(order.data);
            if (profile.symbol) {
                const [news, financialRatios, quotes, employeeCounts, financialStatements] =
                    await Promise.all([
                        getInsightsBySymbol(profile.symbol),
                        getRatiosBySymbol(profile.symbol),
                        getQuotesBySymbol(profile.symbol),
                        getEmployeeCountsBySymbol(profile.symbol),
                        getFinancialStatementsBySymbol(profile.symbol),
                    ]);
                setEmployees(employeeCounts);
                setRatios(financialRatios);
                setQuotes(quotes);
                setFinancialStatements(financialStatements);

                setSupplierNews({
                    price: quotes[0]?.price || 0,
                    changePercentage: quotes[0]?.changePercentage || 0,
                    news,
                });
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const employeeCount = employees?.[0]?.employeeCount;
    const previousEmployeeCount = employees?.[1]?.employeeCount;
    const employeeChange =
        Number((employeeCount - previousEmployeeCount) / previousEmployeeCount) || 0;

    const employeeChangePercentage = roundToTwoDecimals(employeeChange * 100);

    useEffect(() => {
        refreshSupplier(supplierId);
    }, [supplierId]);

    const handleEditClick = (data: SupplierFullCompetitor) => {
        setCompetitor(data);
        setShowCompetitorModal(true);
    };

    const refreshCompetitors = async () => {
        try {
            const data = await getSupplierCompetitors(supplierId);
            setSupplierCompetitors(data);
        } catch (error) {
            handleError(error);
        }
    };

    const handleUpdate = async (data: SupplierFullCompetitor) => {
        try {
            await updateSupplierCompetitor(data);
            await refreshCompetitors();
        } catch (error) {
            handleError(error);
        } finally {
            setShowCompetitorModal(false);
        }
    };

    const loadMoreNews = async (limit: number) => {
        if (!supplierProfile.symbol) return;
        try {
            const news = await getInsightsBySymbol(supplierProfile.symbol, limit);
            setSupplierNews((pre) => ({
                ...pre,
                news,
            }));
        } catch (error) {
            handleError(error);
        }
    };

    const updateVersion = async () => {
        setLoading(true);
        try {
            const res = await updateSupplierAnalysis(supplierId);
            setSupplierProfile(res);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const getOrg = async () => {
        try {
            const org = await getOrganization();
            setOrgName(org.name);
        } catch (error: any) {
            handleError(error);
        }
    };

    useEffect(() => {
        getOrg();
    }, []);

    const incomeStatements =
        period === Period.Annually
            ? financialStatements.incomes?.annually
            : financialStatements.incomes?.quarterly;

    const gradeInfo = getScoreGrade(Number(supplierProfile.analysis?.leverage.score), 10);
    const containerClass = classes[`container-${gradeInfo.tag}`];

    const panel = (
        <div className={clsx(classes.container, containerClass)}>
            <Spin spinning={loading}>
                <Card bordered={false}>
                    <SupplierDetailTopPanel
                        supplierName={supplierProfile.name}
                        supplierId={supplierId}
                        showUpdate={!supplierProfile.analysis}
                        onUpdate={updateVersion}
                    />
                    <div ref={targetRef} style={{ position: "fixed", left: "-1000rem" }}>
                        <PdfPanel
                            profile={supplierProfile}
                            employeeCount={employeeCount}
                            employeeChange={employeeChange}
                            employeeChangePercent={employeeChangePercentage}
                            period={period}
                            incomes={incomeStatements}
                            orderForm={orderForm}
                            competitors={supplierCompetitors}
                            insights={supplierInsights}
                            newsProps={supplierNews}
                            financialStatements={financialStatements}
                            ratios={ratios}
                            orgName={orgName}
                        />
                    </div>
                    <SupplierBase
                        {...supplierProfile}
                        currentEmployee={employeeCount}
                        employeeDifference={employeeChange}
                        showPdfButton={!isPocOnly(me.roles)}
                        onPdfButtonClick={createPdf}
                        quotes={quotes}
                        showStockInfo={false}
                    />
                    <LeverageSummary
                        leverage={supplierProfile.analysis?.leverage}
                        style={{ marginTop: 20 }}
                    />
                    <Tabs
                        className={classes.tabs}
                        items={[
                            {
                                label: "Overview",
                                key: TabKey.Overview,
                                children: (
                                    <>
                                        <CompetitorEditModal
                                            competitor={competitor}
                                            open={showCompetitorModal}
                                            onCancel={() => setShowCompetitorModal(false)}
                                            onOk={handleUpdate}
                                        />

                                        <SupplierOverviewPanel
                                            leftPanenlProps={{
                                                profile: supplierProfile,
                                                competitors: supplierCompetitors,
                                                showCompetitorEdit: isSuperAdmin(me.roles || []),
                                                onEditCompetitor: handleEditClick,
                                                period: period,
                                                onPeriodChange: setPeriod,
                                                incomes: incomeStatements,
                                                analysis: supplierProfile.analysis,
                                                orderForm: orderForm,
                                            }}
                                            rightPanelProps={{
                                                insights: supplierInsights,
                                                financialNewsProps: {
                                                    ...supplierNews,
                                                    onLoadMore: loadMoreNews,
                                                },
                                                orgName: orgName,
                                            }}
                                        />
                                    </>
                                ),
                            },
                            ...(supplierProfile.analysis
                                ? [
                                      {
                                          label: "Market trends",
                                          key: TabKey.Market,
                                          children: (
                                              <MarketTrendsPanel
                                                  incomes={financialStatements.incomes}
                                                  analysis={supplierProfile.analysis}
                                                  employees={employeeCount}
                                                  employeeIncrease={employeeChangePercentage}
                                              />
                                          ),
                                      },
                                      {
                                          label: "Benchmarking",
                                          key: TabKey.Benchmark,
                                          children: (
                                              <BenchmarkingPanel
                                                  analysis={supplierProfile.analysis}
                                              />
                                          ),
                                      },
                                  ]
                                : []),
                            ...(incomeStatements?.length
                                ? [
                                      {
                                          label: "Financial health",
                                          key: TabKey.Health,
                                          children: (
                                              <SupplierRatiosPanel
                                                  ratios={ratios}
                                                  incomes={incomeStatements}
                                              />
                                          ),
                                      },
                                      {
                                          label: "Financial statements",
                                          key: TabKey.Statements,
                                          children: (
                                              <SupplierStatementsPanel
                                                  incomes={financialStatements.incomes}
                                                  balanceSheets={financialStatements.balanceSheets}
                                                  cashFlows={financialStatements.cashFlows}
                                                  showDownload={!isPocOnly(me.roles)}
                                              />
                                          ),
                                      },
                                  ]
                                : []),
                        ]}
                    ></Tabs>
                </Card>
            </Spin>
        </div>
    );

    return panel;
};
